import { useParams, Link } from "react-router-dom";
import { H1, Body } from "@datapay-by-datacom/design-system";
import { DateRangeToolbar } from "../../analytics/components/DateRangeToolbar/DateRangeToolbar";
import { ActiveUsersChart } from "../../analytics/components/ActiveUsersChart/ActiveUsersChart";
import { UserGrowthChart } from "../../analytics/components/UserGrowthChart/UserGrowthChart";
import { ThreadDepthChart } from "../../analytics/components/ThreadDepthChart/ThreadDepthChart";
import { FeedbackChart } from "../../analytics/components/FeedbackChart/FeedbackChart";
import { CompletionsChart } from "../../analytics/components/CompletionsChart/CompletionsChart";
import { CompletionTimeseriesChart } from "../../analytics/components/CompletionTimeseriesChart/CompletionTimeseriesChart";
import { TopicClusterChart } from "../../analytics/components/TopicClusterChart/TopicClusterChart";
import { ChartSkeleton } from "../../analytics/components/ChartSkeleton/ChartSkeleton";
import { CardErrorBoundary } from "../../analytics/components/CardErrorBoundary/CardErrorBoundary";
import { useDateRange } from "../../analytics/hooks/useDateRange";
import { formatShortDate } from "../../analytics/utils/formatters";
import { UnauthorizedMessage } from "../../shared/components";
import { GroupOverviewCards } from "../components/GroupOverviewCards/GroupOverviewCards";
import {
  useGroupOverview,
  useGroupActiveUsers,
  useGroupUserGrowth,
  useGroupCompletionsPerUser,
  useGroupCompletionTimeseries,
  useGroupThreadDepth,
  useGroupFeedbackSummary,
  useGroupTopicClusters,
  useUserManagementPermissions,
} from "../hooks";
import type { Period } from "../../generated/core-api";
import styles from "./GroupAnalyticsScreen.module.css";

type CardKey =
  | "overview"
  | "activeUsers"
  | "userGrowth"
  | "threadDepth"
  | "feedbackSummary"
  | "completionTimeseries"
  | "completionsPerUser"
  | "topicClusters";

function cardSubtitle(
  key: CardKey,
  fromDate: string,
  toDate: string,
  period: Period,
): string {
  const start = formatShortDate(fromDate);
  const end = formatShortDate(toDate);
  const periodName = period.charAt(0).toUpperCase() + period.slice(1);

  const subtitles: Record<CardKey, string> = {
    overview: `Group summary from ${start} to ${end}.`,
    activeUsers: `${periodName} active users from ${start} to ${end}. Active means created a new thread.`,
    userGrowth: `Trend in new member numbers and cumulative member growth from ${start} to ${end}.`,
    threadDepth: `Distribution of thread message counts from ${start} to ${end}.`,
    feedbackSummary: `Thumbs up and down feedback ratio from ${start} to ${end}.`,
    completionTimeseries: `${periodName} completion counts from ${start} to ${end}.`,
    completionsPerUser: `Top members by completion request count from ${start} to ${end}.`,
    topicClusters: `Thread topic clusters via UMAP dimensionality reduction from ${start} to ${end}.`,
  };

  return subtitles[key];
}

function CardShell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className={styles.card}>
      <h3 className={styles.cardTitle}>{title}</h3>
      {subtitle && <p className={styles.cardSubtitle}>{subtitle}</p>}
      <div className={styles.cardBody}>
        <CardErrorBoundary>
          {children ?? <Body>Coming soon</Body>}
        </CardErrorBoundary>
      </div>
    </div>
  );
}

function ErrorPlaceholder({ message }: { message: string }) {
  return <Body>Error: {message}</Body>;
}

export function GroupAnalyticsScreen() {
  const { groupId } = useParams<{ groupId: string }>();
  const { canView } = useUserManagementPermissions();
  const dateRange = useDateRange();

  const resolvedGroupId = groupId ?? "";
  const enabled = canView && !!groupId;

  const overview = useGroupOverview(resolvedGroupId, dateRange.fromDate, dateRange.toDate, enabled);
  const activeUsers = useGroupActiveUsers(
    resolvedGroupId,
    dateRange.fromDate,
    dateRange.toDate,
    dateRange.period,
    enabled,
  );
  const userGrowth = useGroupUserGrowth(resolvedGroupId, dateRange.fromDate, dateRange.toDate, enabled);
  const threadDepth = useGroupThreadDepth(resolvedGroupId, dateRange.fromDate, dateRange.toDate, enabled);
  const feedbackSummary = useGroupFeedbackSummary(resolvedGroupId, dateRange.fromDate, dateRange.toDate, enabled);
  const completionTimeseries = useGroupCompletionTimeseries(
    resolvedGroupId,
    dateRange.fromDate,
    dateRange.toDate,
    dateRange.period,
    enabled,
  );
  const completionsPerUser = useGroupCompletionsPerUser(resolvedGroupId, dateRange.fromDate, dateRange.toDate, enabled);
  const topicClusters = useGroupTopicClusters(resolvedGroupId, dateRange.fromDate, dateRange.toDate, enabled);

  if (!canView) {
    return <UnauthorizedMessage />;
  }

  if (!groupId) {
    return <Body>Group not found.</Body>;
  }

  const sub = (key: CardKey) =>
    cardSubtitle(key, dateRange.fromDate, dateRange.toDate, dateRange.period);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Link to={`/user-management/groups/${groupId}`} className={styles.backLink}>
            &larr; Back to Group
          </Link>
          <H1>Group Analytics</H1>
        </div>
        <DateRangeToolbar
          fromDate={dateRange.fromDate}
          toDate={dateRange.toDate}
          period={dateRange.period}
          isDatePickerOpen={dateRange.isDatePickerOpen}
          onDatePickerOpenChange={dateRange.setDatePickerOpen}
          onDateChange={dateRange.setDateRange}
          onPeriodChange={dateRange.setPeriod}
        />
      </div>

      <section className={styles.fullWidth}>
        <CardShell title="Overview" subtitle={sub("overview")}>
          {overview.isLoading && <ChartSkeleton variant="kpi" />}
          {overview.error && <ErrorPlaceholder message={overview.error.message} />}
          {overview.data && <GroupOverviewCards data={overview.data} />}
        </CardShell>
      </section>

      <section className={styles.grid}>
        <CardShell title="Active Users" subtitle={sub("activeUsers")}>
          {activeUsers.isLoading && <ChartSkeleton variant="chart" />}
          {activeUsers.error && <ErrorPlaceholder message={activeUsers.error.message} />}
          {activeUsers.data && <ActiveUsersChart data={activeUsers.data} />}
        </CardShell>
        <CardShell title="User Growth" subtitle={sub("userGrowth")}>
          {userGrowth.isLoading && <ChartSkeleton variant="chart" />}
          {userGrowth.error && <ErrorPlaceholder message={userGrowth.error.message} />}
          {userGrowth.data && <UserGrowthChart data={userGrowth.data} />}
        </CardShell>
      </section>

      <section className={styles.grid}>
        <CardShell title="Thread Depth" subtitle={sub("threadDepth")}>
          {threadDepth.isLoading && <ChartSkeleton variant="chart" />}
          {threadDepth.error && <ErrorPlaceholder message={threadDepth.error.message} />}
          {threadDepth.data && <ThreadDepthChart data={threadDepth.data} />}
        </CardShell>
        <CardShell title="Feedback Summary" subtitle={sub("feedbackSummary")}>
          {feedbackSummary.isLoading && <ChartSkeleton variant="chart" />}
          {feedbackSummary.error && <ErrorPlaceholder message={feedbackSummary.error.message} />}
          {feedbackSummary.data && <FeedbackChart data={feedbackSummary.data} />}
        </CardShell>
      </section>

      <section className={styles.fullWidth}>
        <CardShell title="Completion Timeseries" subtitle={sub("completionTimeseries")}>
          {completionTimeseries.isLoading && <ChartSkeleton variant="chart" />}
          {completionTimeseries.error && <ErrorPlaceholder message={completionTimeseries.error.message} />}
          {completionTimeseries.data && <CompletionTimeseriesChart data={completionTimeseries.data} />}
        </CardShell>
      </section>

      <section className={styles.fullWidth}>
        <CardShell title="Completions per User" subtitle={sub("completionsPerUser")}>
          {completionsPerUser.isLoading && <ChartSkeleton variant="chart" />}
          {completionsPerUser.error && <ErrorPlaceholder message={completionsPerUser.error.message} />}
          {completionsPerUser.data && <CompletionsChart data={completionsPerUser.data} />}
        </CardShell>
      </section>

      <section className={styles.fullWidth}>
        <CardShell title="Topic Clusters" subtitle={sub("topicClusters")}>
          {topicClusters.isLoading && <ChartSkeleton variant="scatter" />}
          {topicClusters.error && <ErrorPlaceholder message={topicClusters.error.message} />}
          {topicClusters.data && <TopicClusterChart data={topicClusters.data} />}
        </CardShell>
      </section>
    </div>
  );
}
